import { useNavigate } from "react-router-dom";
import { Check, Sparkles, ArrowRight } from "lucide-react";
import { Section, Eyebrow } from "@/components/Section";
import { TIERS } from "@/data/site";

export default function Pricing() {
  const navigate = useNavigate();
  return (
    <div data-testid="pricing-page">
      <section className="bg-ocean">
        <div className="mx-auto max-w-7xl px-6 py-20 text-center md:px-10 md:py-24">
          <Eyebrow>Transparent pricing</Eyebrow>
          <h1 className="mx-auto mt-5 max-w-3xl text-4xl font-bold tracking-tight text-white sm:text-5xl">Pricing</h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-300">Pick the level of testing that fits your supplement. One-time fee — free prepaid shipping included, no subscriptions.</p>
          <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-lime/20 px-4 py-2 text-sm font-semibold text-lime">
            <Sparkles className="h-4 w-4" /> Add more of the same test for just $50 each — up to 20
          </div>
        </div>
      </section>

      <Section>
        <div className="mx-auto grid max-w-3xl gap-6 sm:grid-cols-2">
          {TIERS.map((t) => (
            <div key={t.id} data-testid={`pricing-card-${t.id}`} className={`group relative flex flex-col rounded-3xl border bg-white p-7 transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:shadow-[0_24px_60px_rgba(47,76,120,0.14)] ${t.popular ? "border-navy shadow-[0_12px_40px_rgba(47,76,120,0.12)]" : "border-border"}`}>
              {t.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-lime px-3 py-1 text-xs font-bold text-navy">Most Popular</span>}
              <h3 className="text-lg font-semibold text-ocean">{t.name}</h3>
              <p className="mt-1 text-sm font-semibold text-teal-dark">{t.tagline}</p>
              <p className="mt-5 font-heading text-4xl font-bold text-ocean">${t.price}</p>
              <p className="mt-2 text-sm leading-relaxed text-slate-500">{t.oneLiner}</p>
              <ul className="mt-6 flex-1 space-y-3">
                {t.items.map((it) => (
                  <li key={it} className="flex items-start gap-2.5 text-sm text-slate-600">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-navy" /> {it}
                  </li>
                ))}
              </ul>
              <button onClick={() => navigate(`/checkout?tier=${t.id}`)} data-testid={`pricing-select-${t.id}`}
                className={`mt-7 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold transition-[transform,box-shadow,background-color] duration-200 hover:-translate-y-0.5 active:scale-[0.98] ${t.popular ? "bg-navy text-white hover:bg-navy-dark hover:shadow-[0_12px_28px_rgba(47,76,120,0.22)]" : "border border-navy/20 text-navy hover:bg-secondary"}`}>
                Select {t.name} <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
              </button>
            </div>
          ))}
        </div>

        <div className="mx-auto mt-8 max-w-3xl rounded-2xl border border-border bg-accent/40 p-6 text-center text-sm leading-relaxed text-slate-600" data-testid="pricing-difference">
          <span className="font-semibold text-ocean">Not sure which to pick?</span> <span className="font-semibold text-ocean">Label Accuracy</span> measures <em>one thing</em> — whether the active ingredient is really present at the dose on the label. <span className="font-semibold text-ocean">Full Transparency</span> adds a complete screen for everything that <em>shouldn&rsquo;t</em> be there: allergens, heavy metals, fillers and hidden or undeclared ingredients. If you have allergies or sensitivities, or simply want total peace of mind, Full Transparency is the one to choose.
        </div>

        <div className="mt-10 grid gap-6 md:grid-cols-2">
          <div className="rounded-3xl border border-border bg-white p-7 transition-shadow duration-300 hover:shadow-[0_18px_50px_rgba(47,76,120,0.1)]">
            <h3 className="text-lg font-semibold text-ocean">7-day turnaround, free shipping</h3>
            <p className="mt-2 text-slate-600">Every order includes a <span className="font-semibold text-ocean">free prepaid shipping label</span> and a clear PDF report — typically delivered within <span className="font-semibold text-ocean">7 days</span> of your sample arriving at the lab.</p>
          </div>
          <div className="rounded-3xl border border-navy/15 bg-accent/60 p-7 transition-shadow duration-300 hover:shadow-[0_18px_50px_rgba(47,76,120,0.1)]">
            <h3 className="text-lg font-semibold text-ocean">Testing more than one?</h3>
            <p className="mt-2 text-slate-600">Your first test is full price. Every <span className="font-semibold text-ocean">additional test of the same type is just $50</span> — add up to 20 per order, applied automatically at checkout.</p>
          </div>
        </div>
      </Section>
    </div>
  );
}
